import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { 
  Typography, 
  Box, 
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Link,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

const MarkdownRenderer = ({ 
  content, 
  variant = 'body1',
  sx = {},
  enableGfm = true,
  ...props 
}) => {
  const theme = useTheme();

  // Custom components for rendering markdown elements
  const components = {
    // Headings
    h1: ({ children }) => (
      <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
        {children}
      </Typography>
    ),
    h2: ({ children }) => (
      <Typography variant="h5" component="h2" gutterBottom fontWeight="bold" sx={{ mt: 2, mb: 1 }}>
        {children}
      </Typography>
    ),
    h3: ({ children }) => (
      <Typography variant="h6" component="h3" gutterBottom fontWeight="bold" sx={{ mt: 1.5, mb: 1 }}>
        {children}
      </Typography>
    ),
    h4: ({ children }) => (
      <Typography variant="subtitle1" component="h4" gutterBottom fontWeight="bold" sx={{ mt: 1.5, mb: 0.5 }}>
        {children}
      </Typography>
    ),
    h5: ({ children }) => (
      <Typography variant="subtitle2" component="h5" gutterBottom fontWeight="bold" sx={{ mt: 1, mb: 0.5 }}>
        {children}
      </Typography>
    ),
    h6: ({ children }) => (
      <Typography variant="body1" component="h6" gutterBottom fontWeight="bold" sx={{ mt: 1, mb: 0.5 }}>
        {children}
      </Typography>
    ),

    // Paragraphs
    p: ({ children }) => (
      <Typography variant={variant} paragraph sx={{ lineHeight: 1.6, mb: 1.5 }}>
        {children}
      </Typography>
    ),

    // Strong/Bold text
    strong: ({ children }) => (
      <Typography component="span" fontWeight="bold">
        {children}
      </Typography>
    ),

    // Emphasis/Italic text
    em: ({ children }) => (
      <Typography component="span" fontStyle="italic">
        {children}
      </Typography>
    ),

    // Code blocks
    code: ({ node, inline, className, children, ...props }) => {
      if (inline) {
        return (
          <Typography
            component="code"
            sx={{
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
              padding: '2px 6px',
              borderRadius: 1,
              fontFamily: 'monospace',
              fontSize: '0.9em',
              color: theme.palette.mode === 'dark' ? '#ff6b6b' : '#d63384'
            }}
          >
            {children}
          </Typography>
        );
      }

      return (
        <Paper
          elevation={1}
          sx={{
            p: 2,
            my: 2,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            overflow: 'auto'
          }}
        >
          <Typography
            component="pre"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.9em',
              margin: 0,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
          >
            <code {...props}>{children}</code>
          </Typography>
        </Paper>
      );
    },

    // Blockquotes
    blockquote: ({ children }) => (
      <Paper
        elevation={0}
        sx={{
          borderLeft: `4px solid ${theme.palette.primary.main}`,
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
          p: 2,
          my: 2,
          fontStyle: 'italic'
        }}
      >
        {children}
      </Paper>
    ),

    // Links
    a: ({ href, children }) => (
      <Link 
        href={href} 
        target="_blank" 
        rel="noopener noreferrer"
        sx={{ 
          color: theme.palette.primary.main,
          textDecoration: 'none',
          '&:hover': {
            textDecoration: 'underline'
          }
        }}
      >
        {children}
      </Link>
    ),

    // Horizontal rules
    hr: () => <Divider sx={{ my: 3 }} />,

    // Lists
    ul: ({ children }) => (
      <List dense sx={{ pl: 2, my: 1 }}>
        {children}
      </List>
    ),
    ol: ({ children }) => (
      <List dense component="ol" sx={{ pl: 2, my: 1 }}>
        {children}
      </List>
    ),
    li: ({ children, ordered }) => (
      <ListItem sx={{ display: 'list-item', py: 0.25, px: 0 }}>
        <ListItemText 
          primary={children} 
          primaryTypographyProps={{ 
            variant: variant,
            sx: { lineHeight: 1.5 }
          }}
        />
      </ListItem>
    ),

    // Tables
    table: ({ children }) => (
      <TableContainer component={Paper} elevation={1} sx={{ my: 2 }}>
        <Table size="small">
          {children}
        </Table>
      </TableContainer>
    ),
    thead: ({ children }) => <TableHead>{children}</TableHead>,
    tbody: ({ children }) => <TableBody>{children}</TableBody>,
    tr: ({ children }) => <TableRow>{children}</TableRow>,
    th: ({ children }) => (
      <TableCell 
        component="th" 
        sx={{ 
          fontWeight: 'bold',
          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)'
        }}
      >
        {children}
      </TableCell>
    ),
    td: ({ children }) => <TableCell>{children}</TableCell>,

    // Line breaks
    br: () => <br />
  };

  // Prepare remark plugins
  const remarkPlugins = enableGfm ? [remarkGfm] : [];

  return (
    <Box sx={{ ...sx }} {...props}>
      <ReactMarkdown
        components={components}
        remarkPlugins={remarkPlugins}
      >
        {content || ''}
      </ReactMarkdown>
    </Box>
  );
};

export default MarkdownRenderer;
